import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Check, X, Eye, Download, Package, Wrench, Monitor, CalendarClock } from 'lucide-react';
import { cuotaDeLaLinea, esLineaDeRenting, resumenRenting, tipoEfectivo } from '@/lib/renting';
import { axiosInstance } from '@/App';
import { confirmAsync } from '@/lib/confirm';

export default function ClientPortalQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadQuotes();
  }, []);

  const loadQuotes = async () => {
    try {
      const response = await axiosInstance.get('/client-portal/quotes');
      setQuotes(response.data);
    } catch (error) {
      toast.error('Error al cargar presupuestos');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (quote) => {
    try {
      const response = await axiosInstance.get(`/client-portal/quotes/${quote.id}`);
      setSelectedQuote(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalle del presupuesto');
    }
  };

  const handleApprove = async (quoteId) => {
    if (!(await confirmAsync('¿Estás seguro de que deseas APROBAR este presupuesto?'))) {
      return;
    }

    setProcessing(true);
    try {
      await axiosInstance.post(`/client-portal/quotes/${quoteId}/approve`);
      toast.success('✅ Presupuesto aprobado correctamente');
      setDetailDialogOpen(false);
      loadQuotes();
    } catch (error) {
      toast.error('Error al aprobar presupuesto');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error('Por favor indica la razón del rechazo');
      return;
    }

    setProcessing(true);
    try {
      await axiosInstance.post(`/client-portal/quotes/${selectedQuote.id}/reject`, {
        reason: rejectReason
      });
      toast.success('Presupuesto rechazado');
      setRejectDialogOpen(false);
      setDetailDialogOpen(false);
      setRejectReason('');
      loadQuotes();
    } catch (error) {
      toast.error('Error al rechazar presupuesto');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadPDF = async (quoteId) => {
    try {
      const response = await axiosInstance.get(`/client-portal/quotes/${quoteId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `presupuesto-${quoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Presupuesto descargado');
    } catch (error) {
      toast.error('Error al descargar el presupuesto');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pendiente', class: 'bg-yellow-100 text-yellow-700' },
      draft: { label: 'Pendiente de aprobación', class: 'bg-yellow-100 text-yellow-700' },
      sent: { label: 'Pendiente de aprobación', class: 'bg-blue-100 text-blue-700' },
      accepted: { label: 'Aceptado', class: 'bg-green-100 text-green-700' },
      rejected: { label: 'Rechazado', class: 'bg-red-100 text-red-700' }
    };
    const config = statusMap[status?.toLowerCase()] || statusMap.pending;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  // Get total from quote (handles both field names)
  const getQuoteTotal = (quote) => {
    return quote.total_with_tax || quote.total || 0;
  };

  // Group items by section (licenses, services, equipment)
  const groupItemsBySection = (items) => {
    if (!items || !Array.isArray(items)) return { licenses: [], services: [], equipment: [], renting: [] };
    
    // El renting se aparta PRIMERO y por `renting_meses`, no por tipo ni
    // por seccion: un TPV alquilado sigue siendo un equipo, y si cayera
    // tambien en `equipment` el cliente lo veria dos veces -- una de ellas
    // a 0,00 EUR, porque su subtotal es cero a proposito.
    const renting = items.filter(esLineaDeRenting);
    const facturables = items.filter(item => !esLineaDeRenting(item));
    // Por tipo EFECTIVO: las lineas del 24-sep llevan product_type
    // 'renting' y son equipos.
    const deTipo = (item) => tipoEfectivo({ type: item.product_type });
    return {
      licenses: facturables.filter(item => item.section === 'licenses' || deTipo(item) === 'license'),
      services: facturables.filter(item => item.section === 'services' || deTipo(item) === 'service'),
      equipment: facturables.filter(item => item.section === 'equipment' || deTipo(item) === 'equipment'),
      renting
    };
  };

  /**
   * El renting, que se paga a plazos a una entidad ajena.
   *
   * No reutiliza `renderItemSection` por una razon concreta: esa pinta
   * `item.subtotal || (item.quantity * item.unit_price) || 0`, y en una linea
   * de renting el subtotal vale 0 A PROPOSITO -- no se lo facturamos al
   * cliente. Como 0 es falsy, caeria al calculo de reserva y le ensenaria la
   * cuota bajo el titulo "Subtotal", dentro de un presupuesto cuyo total no
   * la incluye. El cliente no podria cuadrar las cuentas de lo que ve.
   *
   * Angel, 24-sep-2026: "lo que quiere ver el cliente es que le cuesta".
   */
  const renderRentingSection = (items) => {
    if (!items || items.length === 0) return null;
    const resumen = resumenRenting(items);

    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 bg-amber-600 px-4 py-2 rounded-t-lg">
          <CalendarClock className="w-5 h-5 text-white" />
          <h4 className="font-semibold text-white">Renting (cuota mensual)</h4>
        </div>
        <div className="border border-t-0 rounded-b-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 text-sm font-medium text-gray-600">Ref.</th>
                <th className="text-left px-4 py-2 text-sm font-medium text-gray-600">Descripción</th>
                <th className="text-center px-4 py-2 text-sm font-medium text-gray-600">Cant.</th>
                <th className="text-center px-4 py-2 text-sm font-medium text-gray-600">Plazo</th>
                <th className="text-right px-4 py-2 text-sm font-medium text-gray-600">Cuota/mes</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-4 py-3 text-sm text-gray-600">{item.product_ref || '-'}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">
                      {item.product_name || item.description || 'Sin nombre'}
                    </p>
                    {/* Ahi van el mantenimiento incluido y las condiciones de
                        baja anticipada, con sus saltos de linea. */}
                    {item.product_description && (
                      <p className="text-sm text-gray-500 whitespace-pre-line">
                        {item.product_description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-sm">{item.quantity}</td>
                  <td className="px-4 py-3 text-center text-sm">
                    {item.renting_meses ? `${item.renting_meses} meses` : '-'}
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {cuotaDeLaLinea(item).toFixed(2)} €
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-amber-50">
              <tr>
                <td colSpan="4" className="px-4 py-2 text-right font-medium text-gray-700">
                  Cuota de renting:
                </td>
                <td className="px-4 py-2 text-right font-bold text-amber-900">
                  {resumen.cuotaMes.toFixed(2)} €/mes
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          El renting lo financia una entidad ajena, que factura la cuota
          directamente. Este importe no está incluido en el total de abajo.
        </p>
      </div>
    );
  };

  // Render a section of items
  const renderItemSection = (title, items, icon, bgColor, subtotal) => {
    if (!items || items.length === 0) return null;

    const Icon = icon;
    return (
      <div className="mb-6">
        <div className={`flex items-center gap-2 ${bgColor} px-4 py-2 rounded-t-lg`}>
          <Icon className="w-5 h-5" />
          <h4 className="font-semibold text-white">{title}</h4>
        </div>
        <div className="border border-t-0 rounded-b-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 text-sm font-medium text-gray-600">Ref.</th>
                <th className="text-left px-4 py-2 text-sm font-medium text-gray-600">Descripción</th>
                <th className="text-center px-4 py-2 text-sm font-medium text-gray-600">Cant.</th>
                <th className="text-right px-4 py-2 text-sm font-medium text-gray-600">Precio</th>
                <th className="text-right px-4 py-2 text-sm font-medium text-gray-600">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-4 py-3 text-sm text-gray-600">{item.product_ref || '-'}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{item.product_name || item.description || 'Sin nombre'}</p>
                    {item.description && item.product_name && item.description !== item.product_name && (
                      <p className="text-sm text-gray-500">{item.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-sm">{item.quantity}</td>
                  <td className="px-4 py-3 text-right text-sm">{(item.unit_price || 0).toFixed(2)} €</td>
                  <td className="px-4 py-3 text-right font-medium">{(item.subtotal || (item.quantity * item.unit_price) || 0).toFixed(2)} €</td>
                </tr>
              ))}
            </tbody>
            {subtotal !== undefined && subtotal > 0 && (
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="4" className="px-4 py-2 text-right font-medium text-gray-600">Subtotal {title}:</td>
                  <td className="px-4 py-2 text-right font-semibold">{subtotal.toFixed(2)} €</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#9C0F17] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/portal-cliente/dashboard')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <h1 className="text-2xl font-bold">Mis Presupuestos</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Cargando presupuestos...</p>
          </div>
        ) : quotes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-600">No tienes presupuestos disponibles</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quotes.map(quote => (
              <Card key={quote.id} className="hover:shadow-xl transition">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="text-lg">Presupuesto #{quote.quote_number}</span>
                    {getStatusBadge(quote.status)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Fecha</p>
                    <p className="font-semibold">
                      {quote.date ? new Date(quote.date).toLocaleDateString('es-ES') : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-2xl font-bold text-[#9C0F17]">
                      {getQuoteTotal(quote).toFixed(2)} €
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Válido hasta</p>
                    <p className="font-semibold">
                      {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString('es-ES') : 'N/A'}
                    </p>
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={() => handleViewDetail(quote)}
                      className="flex-1"
                      variant="outline"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Ver
                    </Button>
                    <Button
                      onClick={() => handleDownloadPDF(quote.id)}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Action Buttons for quotes awaiting client decision */}
                  {['pending', 'sent', 'draft'].includes(quote.status?.toLowerCase()) && (
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={() => handleApprove(quote.id)}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        disabled={processing}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Aprobar
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedQuote(quote);
                          setRejectDialogOpen(true);
                        }}
                        className="flex-1 bg-red-600 hover:bg-red-700"
                        disabled={processing}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Rechazar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          {selectedQuote && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>Presupuesto #{selectedQuote.quote_number}</span>
                  {getStatusBadge(selectedQuote.status)}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                {/* Quote Info */}
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Fecha</p>
                    <p className="font-semibold">
                      {selectedQuote.date ? new Date(selectedQuote.date).toLocaleDateString('es-ES') : 'No disponible'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Válido hasta</p>
                    <p className="font-semibold">
                      {selectedQuote.valid_until ? new Date(selectedQuote.valid_until).toLocaleDateString('es-ES') : 'No especificado'}
                    </p>
                  </div>
                </div>

                {/* Items grouped by section */}
                <div>
                  {(() => {
                    const grouped = groupItemsBySection(selectedQuote.items);
                    return (
                      <>
                        {renderItemSection(
                          'Licencias', 
                          grouped.licenses, 
                          Package, 
                          'bg-blue-600',
                          selectedQuote.licenses_subtotal
                        )}
                        {renderItemSection(
                          'Servicios', 
                          grouped.services, 
                          Wrench, 
                          'bg-green-600',
                          selectedQuote.services_subtotal
                        )}
                        {renderItemSection(
                          'Equipos', 
                          grouped.equipment, 
                          Monitor, 
                          'bg-orange-600',
                          selectedQuote.equipment_subtotal
                        )}
                        {renderRentingSection(grouped.renting)}
                      </>
                    );
                  })()}
                </div>

                {/* Totals */}
                <div className="bg-gray-100 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Base Imponible</span>
                    <span className="font-semibold">{(selectedQuote.subtotal || 0).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">IVA ({selectedQuote.tax_rate || 21}%)</span>
                    <span className="font-semibold">{(selectedQuote.tax || selectedQuote.tax_amount || 0).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold border-t border-gray-300 pt-2 mt-2">
                    <span>TOTAL</span>
                    <span className="text-[#9C0F17]">{getQuoteTotal(selectedQuote).toFixed(2)} €</span>
                  </div>
                </div>

                {/* Notes */}
                {selectedQuote.notes && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Notas</h4>
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{selectedQuote.notes}</p>
                  </div>
                )}

                {/* Action Buttons */}
                {['pending', 'sent', 'draft'].includes(selectedQuote.status?.toLowerCase()) && (
                  <div className="flex gap-4 pt-4 border-t">
                    <Button
                      onClick={() => handleApprove(selectedQuote.id)}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      disabled={processing}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {processing ? 'Procesando...' : 'Aprobar Presupuesto'}
                    </Button>
                    <Button
                      onClick={() => {
                        setDetailDialogOpen(false);
                        setRejectDialogOpen(true);
                      }}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      disabled={processing}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Rechazar Presupuesto
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar Presupuesto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Razón del rechazo *</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Por favor indica por qué rechazas este presupuesto..."
                rows={4}
                className="mt-2"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleRejectConfirm}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={processing || !rejectReason.trim()}
              >
                {processing ? 'Procesando...' : 'Confirmar Rechazo'}
              </Button>
              <Button
                onClick={() => {
                  setRejectDialogOpen(false);
                  setRejectReason('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
